/**
 * Representative / decision-maker / capacity model.
 *
 * Under the Aged Care Act 2024 supported-decision-making framework, these are
 * DISTINCT roles — a participant may hold several or none of them. This module
 * is the SSOT for deriving WHO may sign the service agreement, so a supporter
 * or portal-only contact can never be recorded as the signatory.
 *
 * Roles map onto existing storage:
 *  - supporter / substitute decision-maker → `supported_decision_makers`
 *  - statements recipient → `clients.representative_receives_statements`
 *  - portal / communication contact → `client_contacts.is_portal_user`
 */

/** Mirrors the `supported_decision_makers.supporter_type` enum. */
export type SupporterType =
  | 'REGISTERED_SUPPORTER'
  | 'DECISION_MAKING_SUPPORTER'
  | 'SUBSTITUTE_DECISION_MAKER';

export const SUPPORTER_TYPES: readonly SupporterType[] = [
  'REGISTERED_SUPPORTER',
  'DECISION_MAKING_SUPPORTER',
  'SUBSTITUTE_DECISION_MAKER',
];

export const SUPPORTER_TYPE_LABELS: Record<SupporterType, string> = {
  REGISTERED_SUPPORTER: 'Registered supporter',
  DECISION_MAKING_SUPPORTER: 'Decision-making supporter',
  SUBSTITUTE_DECISION_MAKER: 'Substitute decision-maker',
};

/** Scope of a substitute decision-maker's authority. Financial scope is what
 * authorises signing the service agreement (a financial/contractual decision). */
export type DecisionScope = 'FINANCIAL' | 'PERSONAL_AND_HEALTH' | 'ALL';

export const DECISION_SCOPES: readonly DecisionScope[] = [
  'FINANCIAL',
  'PERSONAL_AND_HEALTH',
  'ALL',
];

export const DECISION_SCOPE_LABELS: Record<DecisionScope, string> = {
  FINANCIAL: 'Financial',
  PERSONAL_AND_HEALTH: 'Personal & health',
  ALL: 'Financial, personal & health',
};

/** The instrument that confers a substitute decision-maker's authority. */
export type AppointmentBasis =
  | 'ENDURING_POWER_OF_ATTORNEY'
  | 'ENDURING_GUARDIANSHIP'
  | 'GUARDIANSHIP_ORDER'
  | 'ADMINISTRATION_ORDER'
  | 'PERSON_RESPONSIBLE'
  | 'OTHER';

export const APPOINTMENT_BASES: readonly AppointmentBasis[] = [
  'ENDURING_POWER_OF_ATTORNEY',
  'ENDURING_GUARDIANSHIP',
  'GUARDIANSHIP_ORDER',
  'ADMINISTRATION_ORDER',
  'PERSON_RESPONSIBLE',
  'OTHER',
];

export const APPOINTMENT_BASIS_LABELS: Record<AppointmentBasis, string> = {
  ENDURING_POWER_OF_ATTORNEY: 'Enduring Power of Attorney',
  ENDURING_GUARDIANSHIP: 'Enduring Guardianship',
  GUARDIANSHIP_ORDER: 'Guardianship order (tribunal)',
  ADMINISTRATION_ORDER: 'Administration order (tribunal)',
  PERSON_RESPONSIBLE: 'Person responsible (statutory)',
  OTHER: 'Other',
};

/**
 * Participant's own decision-making capacity. The Act PRESUMES capacity; a
 * substitute decision-maker is relied on only when capacity is not present.
 */
export type DecisionCapacity = 'PRESUMED_CAPABLE' | 'SUBSTITUTE_REQUIRED';

export const DECISION_CAPACITIES: readonly DecisionCapacity[] = [
  'PRESUMED_CAPABLE',
  'SUBSTITUTE_REQUIRED',
];

export function hasFinancialScope(scope: string | null | undefined): boolean {
  const s = String(scope ?? '')
    .trim()
    .toUpperCase();
  return s === 'FINANCIAL' || s === 'ALL';
}

/**
 * Appointment instruments that legally confer FINANCIAL decision authority.
 * A guardianship instrument (Enduring Guardianship / Guardianship order) or a
 * statutory "person responsible" covers lifestyle/health decisions only — it
 * can NEVER authorise signing a financial service agreement. `OTHER` and
 * `PERSON_RESPONSIBLE` also FAIL CLOSED — only an explicit Enduring Power of
 * Attorney or Administration order confers financial signing authority, so an
 * ambiguous/unspecified instrument must not silently pass. This is the guard
 * that stops the "Financial scope + Enduring Guardianship basis" mismatch from
 * producing a legally-invalid signatory (Kevin Banham / Graham Oates, GB,
 * 2026-07-08).
 */
const FINANCIAL_APPOINTMENT_BASES = new Set<string>([
  'ENDURING_POWER_OF_ATTORNEY',
  'ADMINISTRATION_ORDER',
]);

export function appointmentBasisConfersFinancialAuthority(
  basis: string | null | undefined,
): boolean {
  const b = String(basis ?? '')
    .trim()
    .toUpperCase();
  return b !== '' && FINANCIAL_APPOINTMENT_BASES.has(b);
}

export type DecisionMakerLike = {
  supporterType: string | null | undefined;
  decisionScope?: string | null;
  /** The authority instrument — REQUIRED for a substitute decision-maker to sign. */
  appointmentBasis?: string | null;
  isActive?: boolean | null;
  name?: string | null;
  /**
   * The `client_contacts` row this representative is linked to. A substitute
   * decision-maker must be linked to a contact before the service-agreement
   * signing request can be addressed to them — the DB enforces this at signing
   * (see `assert_service_agreement_signatory`), so the derivation surfaces it
   * here to keep the UI honest about readiness-to-sign.
   */
  clientContactId?: string | null;
};

export type AgreementSignatory =
  | { kind: 'PARTICIPANT'; name: string }
  | {
      kind: 'SUBSTITUTE_DECISION_MAKER';
      name: string;
      contactLinked: boolean;
      clientContactId: string | null;
    }
  | { kind: 'BLOCKED'; reason: string };

function normalizePersonName(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function activeSubstituteDecisionMakers(
  decisionMakers: readonly DecisionMakerLike[],
): DecisionMakerLike[] {
  return decisionMakers.filter(
    (dm) =>
      dm.isActive === true &&
      String(dm.supporterType ?? '')
        .trim()
        .toUpperCase() === 'SUBSTITUTE_DECISION_MAKER',
  );
}

/**
 * Derive who may sign the SAH service agreement.
 *  - Participant signs when they have capacity for financial/contractual
 *    decisions (the Act's default presumption).
 *  - Otherwise the ACTIVE substitute decision-maker with FINANCIAL (or ALL)
 *    scope signs.
 *  - A supporter or a personal/health-only decision-maker can NEVER sign — if
 *    capacity is absent and no financial SDM exists, signing is BLOCKED.
 */
export function deriveAgreementSignatory(input: {
  capacity: string | null | undefined;
  participantName: string;
  decisionMakers: readonly DecisionMakerLike[];
}): AgreementSignatory {
  const capacity = String(input.capacity ?? 'PRESUMED_CAPABLE')
    .trim()
    .toUpperCase();
  const participantNormalized = normalizePersonName(input.participantName);
  const activeSdms = activeSubstituteDecisionMakers(input.decisionMakers);

  for (const sdm of activeSdms) {
    const sdmNormalized = normalizePersonName(sdm.name);
    if (sdmNormalized && participantNormalized && sdmNormalized === participantNormalized) {
      return {
        kind: 'BLOCKED',
        reason:
          'Decision-maker record matches participant — correct the substitute decision-maker name before issuing the service agreement.',
      };
    }
  }

  if (capacity !== 'SUBSTITUTE_REQUIRED') {
    // Presumed capable (default) → the participant signs. (The name-match guard
    // above already blocks the self-reference case where a recorded SDM carries
    // the participant's own name.)
    return { kind: 'PARTICIPANT', name: input.participantName };
  }

  // Fail CLOSED: a financial substitute decision-maker may sign only when it is
  // explicitly active, has financial (or all) scope, AND carries a recorded
  // authority instrument (appointment basis). Missing/null is not "authorised".
  const financialSdm = input.decisionMakers.find(
    (dm) =>
      dm.isActive === true &&
      String(dm.supporterType ?? '')
        .trim()
        .toUpperCase() === 'SUBSTITUTE_DECISION_MAKER' &&
      hasFinancialScope(dm.decisionScope) &&
      // Fail CLOSED: the appointment instrument must actually confer financial
      // authority — a guardianship basis carries lifestyle/health authority
      // only and can never sign a financial service agreement.
      appointmentBasisConfersFinancialAuthority(dm.appointmentBasis),
  );

  // A financial-scope SDM whose instrument does NOT confer financial authority
  // (e.g. Enduring Guardianship) is a data error, not a valid signatory —
  // surface it explicitly rather than silently blocking with the generic reason.
  const invalidBasisSdm = input.decisionMakers.find(
    (dm) =>
      dm.isActive === true &&
      String(dm.supporterType ?? '')
        .trim()
        .toUpperCase() === 'SUBSTITUTE_DECISION_MAKER' &&
      hasFinancialScope(dm.decisionScope) &&
      !appointmentBasisConfersFinancialAuthority(dm.appointmentBasis),
  );

  if (financialSdm) {
    return {
      kind: 'SUBSTITUTE_DECISION_MAKER',
      name: String(financialSdm.name ?? '').trim() || 'Substitute decision-maker',
      clientContactId: String(financialSdm.clientContactId ?? '').trim() || null,
      // Linked to a client contact ⇒ the signing request can be addressed to
      // them and will pass `assert_service_agreement_signatory`. Unlinked ⇒ the
      // signatory is correct but the agreement cannot yet be sent for signing.
      contactLinked: String(financialSdm.clientContactId ?? '').trim() !== '',
    };
  }

  if (invalidBasisSdm) {
    return {
      kind: 'BLOCKED',
      reason:
        `${String(invalidBasisSdm.name ?? 'The recorded substitute decision-maker').trim()} has financial decision scope but their appointment instrument (${appointmentBasisLabel(invalidBasisSdm.appointmentBasis)}) only confers lifestyle/health authority. Record a financial instrument (Enduring Power of Attorney or Administration order), or add a decision-maker who holds one, before the agreement can be signed.`,
    };
  }

  return {
    kind: 'BLOCKED',
    reason:
      'The participant is not presumed capable and no active substitute decision-maker with financial authority is recorded. A financial EPOA / administrator must be added before the agreement can be signed.',
  };
}

export function appointmentBasisLabel(value: unknown): string {
  const token = String(value ?? '')
    .trim()
    .toUpperCase();
  return (APPOINTMENT_BASIS_LABELS as Record<string, string>)[token] ?? String(value ?? '');
}

export function supporterTypeLabel(value: unknown): string {
  const token = String(value ?? '')
    .trim()
    .toUpperCase();
  return (SUPPORTER_TYPE_LABELS as Record<string, string>)[token] ?? String(value ?? '');
}
